import { useState } from 'react';
import {
  Box,
  Dialog,
  HStack,
  IconButton,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button as ButtonComponent } from '@themes/aegix';
import { HiClipboardCheck, HiClipboardCopy } from 'react-icons/hi';
import { useMutation } from '@tanstack/react-query';
import { toaster } from '../../ui/toaster';
import { rotateScimBearerToken } from '../../../services';

/**
 * Bearer-token rotation modal — Phase 3 of the ADR 019 self-service
 * surface.
 *
 * Two-step flow:
 *
 *   1. **Confirm**: warns the admin that the previous token will stop
 *      working immediately and to be ready to paste the new value into
 *      the IdP. Closing this dialog without confirming aborts the
 *      rotation.
 *   2. **One-time reveal**: shows the freshly generated plaintext
 *      token in a code-formatted box with a copy button. The token is
 *      shown ONCE — Tiaki never displays it again on subsequent reads.
 *      Closing this dialog re-locks the value.
 *
 * Spec: doc/adr/reference/019-real-time-idp-sync-self-service-design.md
 *       (SCIM "Bearer token" + Test buttons sections)
 */

export interface BearerRotateModalProps {
  configId: number;
  open: boolean;
  onClose: () => void;
  /**
   * Called after the rotation succeeds (with the plaintext token already
   * shown in the modal). Use this to invalidate any cached `has_bearer_token`
   * boolean so the parent page re-renders with "configured ✓".
   */
  onRotated?: () => void;
}

export function BearerRotateModal({
  configId,
  open,
  onClose,
  onRotated,
}: BearerRotateModalProps) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const rotation = useMutation({
    mutationFn: () => rotateScimBearerToken(configId),
    onSuccess: (data) => {
      setRevealed(data.generated_bearer_token);
      onRotated?.();
    },
    onError: (err) => {
      toaster.create({
        title: 'Rotation failed',
        description: (err as Error).message,
        type: 'error',
        duration: 8000,
      });
    },
  });

  const handleClose = () => {
    setRevealed(null);
    setCopied(false);
    onClose();
  };

  const handleCopy = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      toaster.create({
        title: 'Copied',
        description: 'Token copied to clipboard.',
        type: 'success',
        duration: 2000,
      });
    } catch (err) {
      toaster.create({
        title: 'Copy failed',
        description: (err as Error).message,
        type: 'error',
        duration: 4000,
      });
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) handleClose();
      }}
      role="alertdialog"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content data-e2e="sso-bearer-rotate-modal">
            <Dialog.Header>
              <Dialog.Title>
                {revealed ? 'New bearer token' : 'Rotate bearer token?'}
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              {revealed ? (
                <VStack align="stretch" gap={3}>
                  <Text fontSize="sm" color="text.primary">
                    Copy this token now and paste it into your IdP's SCIM
                    configuration. Tiaki only stores the SHA-256 hash; this
                    plaintext value will not be shown again.
                  </Text>
                  <HStack gap={2} align="stretch">
                    <Box
                      flex={1}
                      bg="surface.input"
                      borderWidth="1px"
                      borderColor="border.secondary"
                      borderRadius="md"
                      color="text.primary"
                      px={3}
                      py={2}
                      fontFamily="mono"
                      fontSize="sm"
                      wordBreak="break-all"
                      data-e2e="sso-bearer-rotate-revealed"
                    >
                      {revealed}
                    </Box>
                    <IconButton
                      aria-label="Copy bearer token"
                      onClick={handleCopy}
                      variant="outline"
                      size="sm"
                      data-e2e="sso-bearer-rotate-copy"
                    >
                      {copied ? <HiClipboardCheck /> : <HiClipboardCopy />}
                    </IconButton>
                  </HStack>
                  <Text fontSize="xs" color="surface.danger">
                    The previous token has been invalidated. The IdP will fail
                    SCIM pushes until the new token is configured there.
                  </Text>
                </VStack>
              ) : (
                <VStack align="stretch" gap={3}>
                  <Text fontSize="sm" color="text.primary">
                    Rotating the bearer token immediately invalidates the
                    current one. SCIM pushes from the IdP will fail until the
                    new token is pasted into the IdP's configuration.
                  </Text>
                  <Text fontSize="sm" color="text.primary">
                    The new token will be displayed once on the next screen.
                  </Text>
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              {revealed ? (
                <ButtonComponent
                  visual="primary"
                  onClick={handleClose}
                  data-e2e="sso-bearer-rotate-done"
                >
                  Done
                </ButtonComponent>
              ) : (
                <HStack gap={3}>
                  <ButtonComponent visual="secondary" onClick={handleClose}>
                    Cancel
                  </ButtonComponent>
                  <ButtonComponent
                    visual="alert"
                    onClick={() => rotation.mutate()}
                    disabled={rotation.isPending}
                    data-e2e="sso-bearer-rotate-confirm"
                  >
                    {rotation.isPending ? 'Rotating…' : 'Rotate now'}
                  </ButtonComponent>
                </HStack>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default BearerRotateModal;
